import React, { useState, useEffect } from 'react';
import {
  Card,
  Box,
  Text,
  TextField,
  Button,
  InlineStack,
  BlockStack,
  Badge,
  Icon,
  Banner,
} from '@shopify/polaris';
import {
  SettingsMajor,
  CircleTickMajor,
} from '@shopify/polaris-icons';
import { useTranslation } from 'react-i18next';
import api from '../utils/axiosConfig';

/**
 * ZyzfyIntegrationBanner - Dashboard card for connecting TrackingMania to Zetfy
 *
 * This component displays prominently on the dashboard for users who haven't
 * yet linked their Zetfy account, encouraging integration for premium features.
 */
function ZyzfyIntegrationBanner({ onConnectionSuccess }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('loading'); // loading, not_connected, connecting, connected, error
  const [error, setError] = useState('');
  const [integrationData, setIntegrationData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    checkIntegrationStatus();
  }, []);

  const checkIntegrationStatus = async () => {
    try {
      setStatus('loading');
      const response = await api.get('/settings/zyzfy-email');

      if (response.data.integrationStatus === 'active') {
        setStatus('connected');
        setIntegrationData(response.data);
      } else {
        setStatus('not_connected');
        // Pre-fill email if we have one saved
        if (response.data.zyzfyEmail) {
          setEmail(response.data.zyzfyEmail);
        }
      }
    } catch (err) {
      console.error('Error checking integration status:', err);
      setStatus('not_connected');
    }
  };

  const handleConnect = async () => {
    // Validate email
    if (!email || !isValidEmail(email)) {
      setError(t('zyzfyBanner.invalidEmail', 'Please enter a valid email address'));
      return;
    }

    try {
      setStatus('connecting');
      setError('');

      const response = await api.post('/settings/zyzfy-email', {
        zyzfyEmail: email.trim().toLowerCase()
      });

      if (response.data.success) {
        if (response.data.integrationActivated) {
          setStatus('connected');
          setShowSuccess(true);
          setIntegrationData({
            zyzfyEmail: response.data.zyzfyEmail,
            integrationStatus: 'active'
          });

          // Notify parent component if callback provided
          if (onConnectionSuccess) {
            onConnectionSuccess(response.data);
          }

          // Hide success message after 5 seconds
          setTimeout(() => setShowSuccess(false), 5000);
        } else {
          // Email saved but user not found in Zetfy
          setStatus('pending');
          setError(t('zyzfyBanner.emailNotFound',
            'Email saved! If you have a Zetfy account with this email, the connection will be activated automatically.'));
        }
      }
    } catch (err) {
      console.error('Error connecting to Zetfy:', err);
      setStatus('not_connected');
      setError(err.response?.data?.error || t('zyzfyBanner.connectionError', 'Connection failed. Please try again.'));
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Store dismissal in localStorage to persist across sessions
    localStorage.setItem('zyzfy_banner_dismissed', 'true');
  };

  // Don't render if dismissed or already connected (unless showing success)
  if (dismissed && !showSuccess) {
    return null;
  }

  // Loading state
  if (status === 'loading') {
    return null; // Don't show anything while loading to avoid flicker
  }

  // Already connected - show subtle confirmation badge
  if (status === 'connected' && !showSuccess) {
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: '#e3f1df',
        padding: '8px 16px',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <span style={{ fontSize: '16px' }}>✅</span>
        <Text variant="bodySm" as="span" fontWeight="medium">
          {t('zyzfyBanner.connectedStatus', 'Zetfy Integrated')}
        </Text>
      </div>
    );
  }

  // Success state after connection
  if (showSuccess) {
    return (
      <Banner
        title={t('zyzfyBanner.successTitle', 'Zetfy account connected!')}
        tone="success"
        onDismiss={() => setShowSuccess(false)}
      >
        <Text as="p">
          {t('zyzfyBanner.successMessage', 'Your TrackingMania is now linked to Zetfy. Enjoy unlimited orders and advanced features!')}
        </Text>
      </Banner>
    );
  }

  return (
    <Card>
      <Box padding="400">
        <BlockStack gap="400">
          {/* Header with badge */}
          <InlineStack gap="300" align="start" blockAlign="center">
            <div style={{
              backgroundColor: '#f0f0ff',
              padding: '10px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon source={SettingsMajor} color="primary" />
            </div>
            <BlockStack gap="100">
              <InlineStack gap="200" align="start" blockAlign="center">
                <Text variant="headingMd" as="h3">
                  {t('zyzfyBanner.title', 'Are you a Zetfy user?')}
                </Text>
                <Badge tone="attention">
                  {t('zyzfyBanner.badge', 'Premium')}
                </Badge>
              </InlineStack>
              <Text variant="bodyMd" as="p" color="subdued">
                {t('zyzfyBanner.subtitle', 'Connect your account to unlock unlimited orders and advanced tracking features')}
              </Text>
            </BlockStack>
          </InlineStack>

          {/* Benefits list */}
          <div style={{ paddingLeft: '52px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon source={CircleTickMajor} color="success" />
                <Text variant="bodySm" as="span">
                  {t('zyzfyBanner.benefit1', 'Unlimited order tracking')}
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon source={CircleTickMajor} color="success" />
                <Text variant="bodySm" as="span">
                  {t('zyzfyBanner.benefit2', 'Advanced email journeys')}
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon source={CircleTickMajor} color="success" />
                <Text variant="bodySm" as="span">
                  {t('zyzfyBanner.benefit3', 'Priority support')}
                </Text>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <Banner tone={status === 'pending' ? 'info' : 'warning'} onDismiss={() => setError('')}>
              <Text as="p">{error}</Text>
            </Banner>
          )}

          {/* Email input and connect button */}
          <InlineStack gap="300" align="start" wrap={false}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <TextField
                label={t('zyzfyBanner.emailLabel', 'Zetfy account email')}
                labelHidden
                value={email}
                onChange={setEmail}
                type="email"
                placeholder={t('zyzfyBanner.emailPlaceholder', 'your.email@example.com')}
                autoComplete="email"
                disabled={status === 'connecting'}
                error={error && !isValidEmail(email) && email ? t('zyzfyBanner.invalidEmail', 'Invalid email') : undefined}
              />
            </div>
            <div style={{ paddingTop: '0px' }}>
              <Button
                variant="primary"
                onClick={handleConnect}
                loading={status === 'connecting'}
                disabled={!email || status === 'connecting'}
              >
                {t('zyzfyBanner.connectButton', 'Connect Account')}
              </Button>
            </div>
          </InlineStack>

          {/* Footer with link and dismiss */}
          <InlineStack gap="400" align="space-between">
            <Text variant="bodySm" as="p" color="subdued">
              {t('zyzfyBanner.noAccount', "Don't have a Zetfy account?")}{' '}
              <a
                href="https://app.zetfy.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#2c6ecb', textDecoration: 'none' }}
              >
                {t('zyzfyBanner.signUp', 'Sign up for free')}
              </a>
            </Text>
            <Button variant="plain" onClick={handleDismiss}>
              {t('zyzfyBanner.maybeLater', 'Maybe later')}
            </Button>
          </InlineStack>
        </BlockStack>
      </Box>
    </Card>
  );
}

export default ZyzfyIntegrationBanner;
